/**
 * strategyConditions.js
 *
 * Registry mapping strategy names → a function that converts raw condition data
 * (from GET /api/scanner/conditions/{symbol}) into the [{label, st}] array
 * that OpenPositionsTab renders.
 *
 * st values:  'ok'   — condition met (green ✓)
 *             'warn' — condition weakening (amber !)
 *             'fail' — condition broken (red ✕)
 *
 * HOW TO ADD A NEW STRATEGY
 * ─────────────────────────
 * 1. Build the scanner backend for the strategy, storing per-symbol criteria
 *    keys in signal_log (or a new table).
 * 2. Expose it via GET /api/scanner/conditions/{symbol}?strategy=YourStrategy
 *    — returning { filters: {...}, criteria: {...} } in the standard shape.
 * 3. Add an entry here:
 *
 *    'Your Strategy Name': ({ filters, criteria }) => [
 *      { label: 'Condition A', st: criteria.some_key ? 'ok' : 'fail' },
 *      ...
 *    ],
 *
 * The key must exactly match the `strategy` field stored in trade_journal.
 */

export const STRATEGY_CONDITION_REGISTRY = {

  // ── Strategy 1: KAMA Pullback + EMA Trigger ──────────────────────────────
  // Matches trade_journal.strategy = 'KAMA Pullback'
  'KAMA Pullback': ({ filters = {}, criteria = {} }) => [
    // ── Entry filters (core — if these break, the trade premise is gone) ──
    {
      label: 'KAMA daily aligned with weekly',
      st:    filters.kama_align   ? 'ok' : 'fail',
    },
    {
      label: 'Price touched / pulled back to KAMA',
      st:    filters.kama_touched ? 'ok' : 'warn',
    },
    {
      label: 'EMA3 × EMA9 trigger active',
      st:    filters.ema_cross    ? 'ok' : 'warn',
    },
    // ── Confidence criteria ───────────────────────────────────────────────
    {
      label: 'KAMA cross within 20 days',
      st:    criteria.kama_cross    ? 'ok' : 'warn',
    },
    {
      label: 'KAMA cross clean (no re-crossover)',
      st:    criteria.kama_pullback ? 'ok' : 'warn',
    },
    {
      label: 'EMA signal not choppy (≤2 crosses / 10D)',
      st:    criteria.ema_clean     ? 'ok' : 'warn',
    },
    {
      label: 'ATR > 1.5% (stock moves enough)',
      st:    criteria.atr_ok        ? 'ok' : 'warn',
    },
    {
      label: '20D range > 8%',
      st:    criteria.range_ok      ? 'ok' : 'warn',
    },
    {
      label: 'Volume confirmed on crossover day',
      st:    criteria.vol_ok        ? 'ok' : 'warn',
    },
    {
      label: 'RS positive vs NIFTY (10D)',
      st:    criteria.rs_ok         ? 'ok' : 'warn',
    },
  ],

  // ── Strategy 2: Contrarian SIP ───────────────────────────────────────────
  // Conditions for SIP are different — no scanner criteria, just cap + return.
  // OpenPositionsTab handles this strategy separately via the SIP API endpoint.
  'Contrarian SIP': null,   // handled by dedicated SIP path in OpenPositionsTab

  // ── Placeholder ─────────────────────────────────────────────────────────
  // Add new strategies here as you build them.
};

/**
 * Given a strategy name and raw condition data, returns [{label, st}].
 * Returns null if the strategy has no conditions registered.
 */
export function deriveConditions(strategyName, conditionData) {
  const fn = STRATEGY_CONDITION_REGISTRY[strategyName];
  if (!fn) return null;                      // unknown strategy → no conditions
  if (!conditionData) return null;           // still loading / not fetched yet
  if (conditionData.error) return null;      // fetch failed → caller handles
  return fn(conditionData);
}

/** Returns true if a strategy has condition rules registered */
export function strategyHasRules(strategyName) {
  return !!STRATEGY_CONDITION_REGISTRY[strategyName];
}
